import { useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Box, useTheme, Divider, Tooltip } from "@mui/material";
import {
    Done as DoneIcon,
    Report as ReportIcon,
    Download as DownloadIcon,
    Info as InfoIcon
} from "@mui/icons-material";
import { green, orange, pink } from "@mui/material/colors";
import { DateTime } from "luxon";

import { isAuthenticatedMSAL, useMSALPayload } from "@initia-digital/auth-client-utils";
import {
    CriticalOrSiteSpecificHazardListItemReadOnly, GeneralChip,
    MSFileTypeIcon, PlanRegisterHazardClassListItemReadOnly,
    Typography, useSnackbar, BasicLoader,
    FileListItem
} from "@initia-digital/component-library";
import { prodBase, muiPink, muiCommon } from "@initia-digital/colors";

import { baseDefinedAndNotNullNarrowing, pluralize, prepNullableFieldToString, formatDisplayString } from "@initia-digital/ts-utils";
import { compareHazardsByRiskLevel } from "@initia-digital/data-service-mapping";
import { IEquipmentClassUse } from "@initia-digital/types/work-and-safety";

import { getS3Object } from "../api/objectStorage";
import { sortHazardClassesByPresentationOrder, sortPlanRegisterHazardClassPresentationOrder } from "../utils";
import { useHazardClassList, usePlanDetail } from "../hooks";

export const PlanSummary = () => {
    // ----------------------------------------------------

    const { planId } = useParams();
    const theme = useTheme();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { queueSnackbar } = useSnackbar();

    const authDetail = useMSALPayload();
    const isAuthenticated = isAuthenticatedMSAL(authDetail);

    const { planDetailData, planDetailLoading } = usePlanDetail(authDetail, planId!, baseDefinedAndNotNullNarrowing(planId));

    const { hazardClassListData } = useHazardClassList(authDetail, true, { status: 'ACTIVE' });
    const hazardClassListAreDefined = baseDefinedAndNotNullNarrowing(hazardClassListData);

    const referenceHazardClassPresentationOrder = useMemo(() => {
        // ---------------------
        if (!hazardClassListAreDefined) return [];
        return [...hazardClassListData].sort(sortHazardClassesByPresentationOrder).map(hazardClass => hazardClass.label);
    }, [hazardClassListAreDefined, hazardClassListData]);

    const registerHazardsForPresentation = useMemo(() => {
        // ---------------------
        if (!hazardClassListAreDefined || !baseDefinedAndNotNullNarrowing(planDetailData)) return [];
        return [...planDetailData.hazardsFromRegister]
            .sort((a, b) => compareHazardsByRiskLevel(a, b))
            .sort((a, b) => sortPlanRegisterHazardClassPresentationOrder(a, b, referenceHazardClassPresentationOrder));
    }, [hazardClassListAreDefined, planDetailData, referenceHazardClassPresentationOrder]);

    const hazardClassGroups = useMemo(() => {
        // ---------------------
        if (!hazardClassListAreDefined) return {};
        return Object.groupBy(registerHazardsForPresentation, (hazard) => hazard.hazardClass.label);
    }, [registerHazardsForPresentation, hazardClassListAreDefined]);

    const registerHazardGroups = useMemo(() => {
        // ---------------------
        if (!hazardClassListAreDefined) return [];
        return Object.keys(hazardClassGroups);
    }, [hazardClassGroups, hazardClassListAreDefined]);

    // Get creator name if authenticated
    const creatorName = useMemo(() => {
        if (!baseDefinedAndNotNullNarrowing(authDetail)) return null;
        return authDetail.msal.accounts[0].name ?? null;
    }, [authDetail]);

    // Combine creator with team members, ensuring no duplicates
    const allTeamMembers = useMemo(() => {
        if (!baseDefinedAndNotNullNarrowing(planDetailData)) return [];
        const members = new Set(planDetailData.teamMembers);
        if (baseDefinedAndNotNullNarrowing(creatorName)) {
            members.add(creatorName);
        }
        return Array.from(members);
    }, [planDetailData, creatorName]);

    if (planDetailLoading || !baseDefinedAndNotNullNarrowing(planDetailData)) {
        return (
            <Box px="5px" display="flex" flexDirection="column" justifyContent="center" alignItems="center" mt="15px">
                <Typography fontSize="0.8rem">Loading plan detail...</Typography>
                <BasicLoader size={50} />
            </Box>
        );
    }

    const {
        objectives,
        description, supportingContext,
        startDate, endDate,
        ourContact,
        serviceClearanceRequired,
        testsRequired, testSchedule,
        installationsRequired, installationSchedule,
        bookings, equipment,
        fileUploads,
        criticalHazards, hazardsFromRegister
    } = planDetailData;

    const shouldCalculateDuration = baseDefinedAndNotNullNarrowing(startDate) && baseDefinedAndNotNullNarrowing(endDate);
    const duration = shouldCalculateDuration ? DateTime.fromISO(endDate).endOf('day').diff(DateTime.fromISO(startDate).startOf('day'), 'days', { conversionAccuracy: 'longterm' }) : null;

    const hasSiteInvestigationPlan = fileUploads.filter(file => file.name.toLowerCase().replace('_', '').replace('-', '').includes('investigation plan') && file.includeInAppendix).length > 0;
    const serviceClearanceBooked = bookings.rows.filter(booking => booking.bookingType === 'Service clearance').length > 0;

    // Get existing query params and merge with new ones
    const currentParams = Object.fromEntries(searchParams.entries());

    return (
        <Box display="flex" flexDirection="column">
            <Box
                display="flex" flexDirection="row" alignItems="center"
                sx={{ cursor: "pointer", border: '1px solid transparent', "&:hover": { border: "1px solid lightgrey" } }}
                onClick={() => navigate({
                    search: new URLSearchParams({
                        ...currentParams,
                        stage: 'overview'
                    }).toString()
                })}
            >
                <Typography
                    fontSize="0.9rem"
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    {`1. Overview`}
                </Typography>
            </Box>
            {
                objectives.length > 0 &&
                <Box
                    display="flex" flexDirection="row" rowGap="5px"
                    my="10px"
                    flexWrap="wrap" >
                    {
                        objectives.map((objective, idx) => {
                            return (
                                <Box key={`objective-${idx}`}>
                                    <GeneralChip
                                        label={objective}
                                        backgroundColor={theme.palette.secondary.main}
                                        size="vsmall"
                                    />
                                </Box>
                            )
                        })
                    }
                </Box>
            }
            <Box
                display="flex" flexDirection="column" justifyContent="center"
                my="10px" mx="8px"
            >
                <Typography
                    color={theme.palette.secondary.main}
                    fontWeight={700}
                    fontSize="0.7rem"
                >
                    Description
                </Typography>
                <Typography
                    color={theme.palette.primary.contrastText}
                    fontWeight={300}
                    fontSize="0.75rem"
                >
                    {`${prepNullableFieldToString(description) !== '' ? prepNullableFieldToString(description) : 'No description'}`}
                </Typography>
            </Box>
            <Box my="10px">
                <Divider />
            </Box>
            <Box
                display="flex" flexDirection="column" justifyContent="center"
                mx="8px"
            >
                <Typography
                    color={theme.palette.secondary.main}
                    fontWeight={700}
                    fontSize="0.7rem"
                >
                    Other relevant information
                </Typography>
                <Typography
                    color={theme.palette.primary.contrastText}
                    fontSize="0.75rem"
                    fontWeight={300}
                >{`${prepNullableFieldToString(supportingContext) !== '' ? prepNullableFieldToString(supportingContext) : 'No other relevant information'}`}</Typography>
            </Box>
            <Box my="10px">
                <Divider />
            </Box>
            <Box
                display="flex" flexDirection="column" justifyContent="center"
                mx="8px"
            >
                <Typography
                    color={theme.palette.secondary.main}
                    fontSize="0.7rem"
                    fontWeight={700}
                >
                    When is the work?
                </Typography>
                <Typography
                    color={theme.palette.primary.contrastText}
                    fontSize="0.75rem"
                    fontWeight={300}
                >{`${DateTime.fromISO(startDate).toFormat('dd/MM/yyyy')} - ${DateTime.fromISO(endDate).toFormat('dd/MM/yyyy')} ${shouldCalculateDuration ? `(${Math.round(duration!.days)} day${Math.round(duration!.days) === 1 ? '' : 's'})` : ''}`}</Typography>
            </Box>
            <Box
                display="flex" flexDirection="row" alignItems="center"
                mt="15px"
                sx={{ cursor: "pointer", border: '1px solid transparent', "&:hover": { border: "1px solid lightgrey" } }}
                onClick={() => navigate({
                    search: new URLSearchParams({
                        ...currentParams,
                        stage: 'overview'
                    }).toString()
                })}
            >
                <Typography
                    fontSize="0.9rem"
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    {`2. Team`}
                </Typography>
            </Box>
            {
                allTeamMembers.length === 0 && !baseDefinedAndNotNullNarrowing(ourContact)
                && (
                    <Box
                        display="flex" flexDirection="row"
                        my="10px" mx="8px" flexWrap="wrap"
                    >
                        <Typography
                            color={theme.palette.primary.contrastText}
                            fontSize="0.75rem"
                            fontWeight={300}
                        >
                            No team members
                        </Typography>
                    </Box>
                )
            }
            {
                (allTeamMembers.length > 0 || baseDefinedAndNotNullNarrowing(ourContact))
                && (
                    <Box
                        display="flex" flexDirection="row" flexWrap="wrap"
                        my="10px" mx="8px" gap="8px"
                    >
                        {/* Contact person first if exists */}
                        {ourContact && (
                            <GeneralChip
                                key={`contact-selected-${ourContact}`}
                                label={ourContact}
                                backgroundColor={muiPink[500]}
                                color={muiCommon.white}
                                size="vsmall"
                            />
                        )}
                        {/* Remaining team members */}
                        {allTeamMembers
                            .filter(member => member !== ourContact)
                            .map((member, idx) => (
                                <GeneralChip
                                    key={`team-member-${idx}`}
                                    label={member}
                                    backgroundColor={prodBase.primary.main}
                                    color={prodBase.primary.contrastText}
                                    size="vsmall"
                                />
                            ))
                        }
                    </Box>
                )
            }
            <Box
                display="flex" flexDirection="row" alignItems="center"
                mt="15px"
                sx={{ cursor: "pointer", border: '1px solid transparent', "&:hover": { border: "1px solid lightgrey" } }}
                onClick={() => navigate({
                    search: new URLSearchParams({
                        ...currentParams,
                        stage: 'workbrief'
                    }).toString()
                })}
            >
                <Typography
                    fontSize="0.9rem"
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    {`3. Workbrief`}
                </Typography>
            </Box>
            {
                serviceClearanceRequired
                && (
                    <Box
                        display="flex" flexDirection="column"
                        my="5px" mx="8px"
                    >
                        <Box
                            display="flex" flexDirection="row"
                            gap={1} alignItems="center"
                            justifyContent="space-between"
                            width="100%"
                        >
                            <Box display="flex" flexDirection="row" gap={1} alignItems="center" flex={1} minWidth={0} sx={{ overflow: 'hidden' }}>
                                <ReportIcon sx={{ color: pink['500'], flexShrink: 0 }} />
                                <Typography
                                    fontSize="0.75rem"
                                    color={pink['500']}
                                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                    {`Initia's service clearance standard operating procedure applies`}
                                </Typography>
                            </Box>
                            <Tooltip 
                                title="This provides guidelines and procedures for minimising risks while conducting these types of activities"
                                placement="right"
                                arrow
                            >
                                <InfoIcon 
                                    sx={{ 
                                        fontSize: "1rem", 
                                        color: pink['500'],
                                        cursor: 'help',
                                        flexShrink: 0,
                                        ml: 1
                                    }} 
                                />
                            </Tooltip>
                        </Box>
                        
                        <Box mb="8px" my="8px">
                        <FileListItem
                            filename={`Initia Service Clearance SOP Rev5.pdf`}
                            selected={false}
                            onClick={async () => {
                                try {
                                    // Load the PDF from the public folder
                                    const response = await fetch('/Initia Service Clearance SOP Rev5.pdf');
                                    if (!response.ok) {
                                        throw new Error('Failed to load SOP PDF');
                                    }
                                    const blob = await response.blob();
                                    
                                    // Create a download link and trigger it
                                    const url = window.URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.download = 'Initia Service Clearance SOP Rev5.pdf';
                                    document.body.appendChild(link);
                                    link.click();
                                    
                                    // Cleanup
                                    document.body.removeChild(link);
                                    window.URL.revokeObjectURL(url);
                                } catch (error) {
                                    console.error('Error downloading SOP PDF:', error);
                                    queueSnackbar('Error downloading Service Clearance SOP', 'error', 'Error', 3000);
                                }
                            }}
                        />
                        </Box>

                        <Box
                            display="flex" flexDirection="row"
                            gap={1} alignItems="center"
                            justifyContent="space-between"
                            width="100%"
                        >
                            <Box display="flex" flexDirection="row" gap={1} alignItems="center" flex={1} minWidth={0} sx={{ overflow: 'hidden' }}>
                                {
                                    hasSiteInvestigationPlan ?
                                    <DoneIcon sx={{ color: green['500'], flexShrink: 0 }} />
                                    :
                                    <ReportIcon sx={{ color: hasSiteInvestigationPlan ? green['500'] : pink['500'], flexShrink: 0 }} />
                                }
                                <Typography
                                    fontSize="0.75rem"
                                    color={hasSiteInvestigationPlan ? green['500'] : pink['500']}
                                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                    {hasSiteInvestigationPlan
                                        ? "An investigation plan has been appended to this plan"
                                        : "An investigation plan needs to be appended (or Director approval)"}
                                </Typography>
                            </Box>
                            <Tooltip 
                                title="1) Go to 'Site plans and permits' and upload your plan. 2) Ensure that 'investigation plan' is in the filename and it is appended to this plan"
                                placement="right"
                                arrow
                            >
                                <InfoIcon 
                                    sx={{ 
                                        fontSize: "1rem", 
                                        color: hasSiteInvestigationPlan ? green['500'] : pink['500'],
                                        cursor: 'help',
                                        flexShrink: 0,
                                        ml: 1
                                    }} 
                                />
                            </Tooltip>
                        </Box>

                        <Box
                            display="flex" flexDirection="row"
                            gap={1} alignItems="center"
                            justifyContent="space-between"
                            width="100%"
                        >
                            <Box display="flex" flexDirection="row" gap={1} alignItems="center" flex={1} minWidth={0} sx={{ overflow: 'hidden' }}>
                                {
                                    serviceClearanceBooked ?
                                    <DoneIcon sx={{ color: green['500'], flexShrink: 0 }} />
                                    : <ReportIcon sx={{ color: serviceClearanceBooked ? green['500'] : pink['500'], flexShrink: 0 }} />
                                }
                                <Typography
                                    fontSize="0.75rem"
                                    color={serviceClearanceBooked ? green['500'] : pink['500']}
                                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                    {serviceClearanceBooked
                                        ? "Service clearance has been booked"
                                        : "No service clearance booking (Director approval required)"}
                                </Typography>
                            </Box>
                            <Tooltip 
                                title="Director approval is required before this work can commence without a service clearance"
                                placement="right"
                                arrow
                            >
                                <InfoIcon 
                                    sx={{ 
                                        fontSize: "1rem", 
                                        color: serviceClearanceBooked ? green['500'] : pink['500'],
                                        cursor: 'help',
                                        flexShrink: 0,
                                        ml: 1
                                    }} 
                                />
                            </Tooltip>
                        </Box>
                    </Box>
                )
            }
            {
                !serviceClearanceRequired
                && (
                    <Box
                        display="flex" flexDirection="row"
                        gap={1} alignItems="center"
                        my="5px"
                        mx="8px"
                    >
                        <ReportIcon sx={{ color: orange['500'] }} />
                        <Typography
                            fontSize="0.75rem"
                            color={orange['500']}
                        >
                            Our activities are not expected to disturb the ground or overhead space.
                        </Typography>
                    </Box>
                )
            }
            {
                testsRequired && testSchedule.rows.length > 0
                && (
                    <Box
                        display="flex" flexDirection="column"
                        my="10px" mx="8px" 
                    >
                        <Box
                            display="flex" flexDirection="row" gap={1} alignItems="center"
                        >
                            <Typography
                                color={theme.palette.secondary.main}
                                fontSize="0.7rem"
                                fontWeight={700}
                            >
                                Tests required:
                            </Typography>
                        </Box>
                        <Box
                            display="flex" flexDirection="row" flexWrap="wrap"
                            pl="15px" mx="8px" 
                        >
                            {
                                testSchedule.rows.map((testItem, idx) => {
                                    return (
                                        <Box key={`test-item-${idx}`} m="5px">
                                            <Typography
                                                fontSize="0.75rem"
                                                fontWeight={300}
                                                color={theme.palette.primary.contrastText}
                                            >
                                                <strong>{`${testItem.testQuantity ?? 0}`}</strong>&nbsp;{`${testItem.testType && testItem.testType !== '' ? pluralize(testItem.testType, testItem.testQuantity ?? 0) : ''}`}
                                            </Typography>
                                        </Box>
                                    )
                                })
                            }
                        </Box>
                    </Box>
                )
            }
            {
                (!testsRequired || testSchedule.rows.length === 0)
                && (
                    <Box
                        display="flex" flexDirection="row" alignItems="center"
                        my="10px" gap={1} mx="8px"
                    >
                        <ReportIcon sx={{ color: orange['500'] }} />
                        <Typography
                            fontSize={"0.75rem"} color={orange['500']}
                        >
                            No specific testing is included
                        </Typography>
                    </Box>
                )
            }
            {
                installationsRequired
                && (
                    <Box
                        display="flex" flexDirection="column"
                        my="10px" mx="8px"
                    >
                        <Box
                            display="flex" flexDirection="row" gap={1} alignItems="center"
                        >
                            <Typography
                                color={theme.palette.secondary.main}
                                fontSize="0.7rem"
                                fontWeight={700}
                            >
                                Installations required:
                            </Typography>
                        </Box>
                        <Box
                            display="flex" flexDirection="row" flexWrap="wrap"
                            pl="15px"
                        >
                            {
                                installationSchedule.rows.map((installationItem, idx) => {
                                    return (
                                        <Box key={`installation-item-${idx}`} m="5px">
                                            <Typography
                                                fontSize="0.75rem"
                                                fontWeight={300}
                                                color={theme.palette.primary.contrastText}
                                            >
                                                <strong>{`${installationItem.installationQuantity ?? 0}`}</strong>&nbsp;{`${installationItem.installationType && installationItem.installationType !== '' ? pluralize(installationItem.installationType, installationItem.installationQuantity ?? 0) : ''}`}
                                            </Typography>
                                        </Box>
                                    )
                                })
                            }
                        </Box>
                    </Box>
                )
            }
            {
                (!installationsRequired || installationSchedule.rows.length === 0)
                && (
                    <Box
                        display="flex" flexDirection="row" alignItems="center"
                        my="10px" gap={1} mx="8px"
                    >
                        <ReportIcon sx={{ color: orange['500'] }} />
                        <Typography
                            fontSize={"0.75rem"} color={orange['500']}
                        >
                            No specific installations are included
                        </Typography>
                    </Box>
                )
            }
            {
                bookings.rows.length > 0
                && (
                    <Box
                        display="flex" flexDirection="column"
                        my="10px" mx="8px"
                    >
                        <Box
                            display="flex" flexDirection="row" gap={1} alignItems="center"
                        >
                            <Typography
                                color={theme.palette.secondary.main}
                                fontSize="0.7rem"
                                fontWeight={700}
                            >
                                Bookings:
                            </Typography>
                        </Box>
                        <Box
                            display="flex" flexDirection="row" flexWrap="wrap"
                            pl="15px"
                        >
                            {
                                bookings.rows.map((bookingItem, idx) => {
                                    return (
                                        <Box key={`booking-item-${idx}`} m="5px">
                                            <Typography
                                                fontSize="0.75rem"
                                                fontWeight={300}
                                                color={theme.palette.primary.contrastText}
                                            >
                                                <strong>{`${bookingItem.contractorOrSubcontractor}`}</strong>&nbsp;{`(${bookingItem.bookingType})`}
                                            </Typography>
                                            <Typography
                                                fontSize="0.7rem"
                                                fontWeight={300}
                                                color={theme.palette.primary.contrastText}
                                            >
                                                {`${prepNullableFieldToString(bookingItem.bookingNotes)}`}
                                            </Typography>
                                        </Box>
                                    )
                                })
                            }
                        </Box>
                    </Box>
                )
            }
            {
                bookings.rows.length === 0
                && (
                    <Box
                        display="flex" flexDirection="row" alignItems="center"
                        my="10px" gap={1} mx="8px"
                    >
                        <ReportIcon sx={{ color: orange['500'] }} />
                        <Typography
                            fontSize={"0.75rem"} color={orange['500']}
                        >
                            No bookings have been recorded
                        </Typography>
                    </Box>
                )
            }
            <Box
                display="flex" flexDirection="row" alignItems="center"
                mt="15px"
                sx={{ cursor: "pointer", border: '1px solid transparent', "&:hover": { border: "1px solid lightgrey" } }}
                onClick={() => navigate({
                    search: new URLSearchParams({
                        ...currentParams,
                        stage: 'sitePlansAndPermits'
                    }).toString()
                })}
            >
                <Typography
                    fontSize="0.9rem"
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    {`4. Site plans and permits`}
                </Typography>
            </Box>
            {
                fileUploads.length === 0
                && (
                    <Box
                        display="flex" flexDirection="row" alignItems="center"
                        my="10px" gap={1} mx="8px"
                    >
                        <ReportIcon sx={{ color: orange['500'] }} />
                        <Typography
                            fontSize={"0.75rem"} color={orange['500']}
                        >
                            No file uploads
                        </Typography>
                    </Box>
                )
            }
            {
                fileUploads.length > 0 && (
                    <Box
                        display="flex" flexDirection="column"
                        my="10px" mx="8px"
                        sx={{
                            containerType: 'inline-size',
                            containerName: 'file-uploads-plan-summary',
                            width: '100%'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gap: '10px',
                                '@container file-uploads-plan-summary (min-width: 550px)': {
                                    gridTemplateColumns: 'repeat(2, 1fr)'
                                },
                                '@container file-uploads-plan-summary (max-width: 549px)': {
                                    gridTemplateColumns: '1fr'
                                }
                            }}
                        >
                            {fileUploads.map((fileUpload, idx) => {
                                return (
                                    <Box
                                        key={`plan-summary-file-upload-${idx}`}
                                        display="flex" flexDirection="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        sx={{
                                            cursor: "pointer",
                                            border: '1px solid transparent',
                                            "&:hover": { border: "1px solid lightgrey" },
                                            '&:hover .download-icon': {
                                                opacity: 1
                                            }
                                        }}
                                        onClick={async () => {
                                            // --------------------------------
                                            if (isAuthenticated && baseDefinedAndNotNullNarrowing(fileUpload.path)) {
                                                const response = await getS3Object(authDetail, { path: fileUpload.path });
                                                // ---------------------
                                                if (response?.buffer) {
                                                    try {
                                                        // ---------------------
                                                        const blob = new Blob([response.buffer], { type: response.mimeType ?? 'application/octet-stream' });
                                                        // ---------------------
                                                        // Create URL for the blob
                                                        const url = window.URL.createObjectURL(blob);
                                                        // ---------------------
                                                        // Create temporary link element
                                                        const link = document.createElement('a');
                                                        link.href = url;
                                                        link.download = fileUpload.name;
                                                        // ---------------------
                                                        // Trigger download
                                                        document.body.appendChild(link);
                                                        link.click();
                                                        queueSnackbar(`File saved - check your downloads`, 'success', 'Success', 3000, '1rem');
                                                        // ---------------------
                                                        // Cleanup
                                                        document.body.removeChild(link);
                                                        window.URL.revokeObjectURL(url);
                                                    } catch (error) {
                                                        console.error('Error creating object URL:', error);
                                                        queueSnackbar(`Error getting: ${fileUpload.name}`, 'error', 'Error', 3000, '1rem');
                                                        throw error;
                                                    }
                                                }
                                            }
                                        }}
                                    >
                                        <Box
                                            key={`action-a-plan-file-upload-${idx}`}
                                            display="flex" flexDirection="column"
                                            flexWrap="wrap"
                                        >
                                            <Box
                                                display="flex" flexDirection="row" alignItems="center"
                                            >
                                                <MSFileTypeIcon
                                                    extension={fileUpload.name.split('.').pop()}
                                                    size={24}
                                                />
                                                &nbsp;
                                                <Typography
                                                    color={theme.palette.primary.contrastText}
                                                    fontSize="0.75rem"
                                                    fontWeight={300}
                                                >
                                                    {fileUpload.name}
                                                </Typography>
                                            </Box>
                                            <Box
                                                display="flex" flexDirection="row" alignItems="center"
                                                pl="30px"
                                            >
                                                <Typography
                                                    color={theme.palette.primary.contrastText}
                                                    fontSize={"0.6rem"}
                                                    fontWeight={300}
                                                >
                                                    {fileUpload.notes}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box
                                            className="download-icon"
                                            sx={{
                                                opacity: 0,
                                                mx: "10px",
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <DownloadIcon
                                                sx={{
                                                    fontSize: '1rem',
                                                    color: 'grey'
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                )
                            })}
                        </Box>
                    </Box>
                )
            }
            <Box
                display="flex" flexDirection="row" alignItems="center"
                mt="15px"
                sx={{ cursor: "pointer", border: '1px solid transparent', "&:hover": { border: "1px solid lightgrey" } }}
                onClick={() => navigate({
                    search: new URLSearchParams({
                        ...currentParams,
                        stage: 'hazardAssessment'
                    }).toString()
                })}
            >
                <Typography
                    fontSize="0.9rem"
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    {`5. Hazard assessment`}
                </Typography>
            </Box>
            {
                criticalHazards.length === 0 && (
                    <Box
                        display="flex" flexDirection="row" alignItems="center"
                        my="5px" gap={1} mx="8px"
                    >
                        <ReportIcon sx={{ color: orange['500'] }} />
                        <Typography
                            fontSize={"0.75rem"} color={orange['500']}
                        >
                            No highlighted or site specific hazards recorded
                        </Typography>
                    </Box>
                )
            }
            {
                criticalHazards.length > 0
                && (
                    <Box
                        display="flex" flexDirection="column"
                        my="5px"
                        p="4px"
                        sx={{
                            containerType: 'inline-size',
                            containerName: `critical-hazard-plan-summary`,
                            width: '100%'
                        }}
                    >
                        <Box
                            display="flex" flexDirection="row" gap={1} alignItems="center"
                            pb="5px"
                        >
                            <Typography
                                color={theme.palette.secondary.main}
                                fontSize="0.7rem"
                                fontWeight={700}
                            >
                                {`Highlighted or site specific hazards (${criticalHazards.length})`}
                            </Typography>
                        </Box>
                        {
                            criticalHazards.length > 0 &&
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: '10px',
                                    '@container critical-hazard-plan-summary (min-width: 700px)': {
                                        gridTemplateColumns: 'repeat(2, 1fr)'
                                    },
                                    '@container critical-hazard-plan-summary (max-width: 699px)': {
                                        gridTemplateColumns: '1fr'
                                    }
                                }}
                            >
                                {criticalHazards.map(criticalHazard => {
                                    // --------------------------
                                    return (
                                        <CriticalOrSiteSpecificHazardListItemReadOnly
                                            key={criticalHazard.id}
                                            hazard={criticalHazard}
                                        />
                                    )
                                })}
                            </Box>
                        }
                    </Box>
                )
            }
            {
                hazardsFromRegister.length === 0 && (
                    <Box
                        display="flex" flexDirection="row" alignItems="center"
                        my="5px" gap={1} mx="8px"
                    >
                        <ReportIcon sx={{ color: pink['500'] }} />
                        <Typography
                            fontSize={"0.75rem"} color={pink['500']}
                        >
                            No register hazards recorded
                        </Typography>
                    </Box>
                )
            }
            {
                hazardsFromRegister.length > 0
                && (
                    <Box
                        display="flex" flexDirection="column"
                        my="5px"
                        p="4px"
                        sx={{
                            containerType: 'inline-size',
                            containerName: `register-hazard-plan-summary`,
                            width: '100%'
                        }}
                    >
                        <Box
                            display="flex" flexDirection="row" gap={1} alignItems="center"
                            pb="5px"
                        >
                            <Typography
                                color={theme.palette.secondary.main}
                                fontSize="0.7rem"
                                fontWeight={700}
                            >
                                {`Register hazard groups (${registerHazardGroups.length} group${registerHazardGroups.length === 1 ? '' : 's'}, ${hazardsFromRegister.length} hazard${hazardsFromRegister.length === 1 ? '' : 's'})`}
                            </Typography>
                        </Box>
                        {
                            hazardsFromRegister.length > 0 &&
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: '10px',
                                    '@container register-hazard-plan-summary (min-width: 700px)': {
                                        gridTemplateColumns: 'repeat(2, 1fr)'
                                    },
                                    '@container register-hazard-plan-summary (max-width: 699px)': {
                                        gridTemplateColumns: '1fr'
                                    }
                                }}
                            >
                                {
                                    registerHazardGroups.map(hazardClassLabel => {
                                        // ---------------------
                                        return (
                                            <PlanRegisterHazardClassListItemReadOnly
                                                key={`plan-summary-register-hazard-group-${hazardClassLabel}`}
                                                hazardClassLabel={hazardClassLabel}
                                                hazards={hazardClassGroups[hazardClassLabel] ?? []}
                                            />
                                        )
                                    })
                                }
                            </Box>
                        }
                    </Box>
                )
            }
            <Box
                display="flex" flexDirection="row" alignItems="center"
                mt="15px"
                sx={{ cursor: "pointer", border: '1px solid transparent', "&:hover": { border: "1px solid lightgrey" } }}
                onClick={() => navigate({
                    search: new URLSearchParams({
                        ...currentParams,
                        stage: 'ppeAndTools'
                    }).toString()
                })}
            >
                <Typography
                    fontSize="0.9rem"
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    {`6. PPE and tools`}
                </Typography>
            </Box>
            {
                equipment.length === 0 && (
                    <Box
                        display="flex" flexDirection="row" alignItems="center"
                        my="5px" gap={1} mx="8px"
                    >
                        <ReportIcon sx={{ color: orange['500'] }} />
                        <Typography
                            fontSize={"0.75rem"} color={orange['500']}
                        >
                            No PPE and tools recorded
                        </Typography>
                    </Box>
                )                
            }
            {
                equipment.length > 0 && (
                    <Box
                        display="flex" flexDirection="column"
                        my="5px" mx="8px"
                        sx={{
                            containerType: 'inline-size',
                            containerName: `equipment-plan-summary`,
                            width: '100%'
                        }}
                    >
                        <Box
                            display="flex" flexDirection="row" gap={1} alignItems="center"
                            pb="5px"
                        >
                            <Typography
                                color={theme.palette.secondary.main}
                                fontSize="0.7rem"
                                fontWeight={700}
                            >
                                {`PPE and tools (${equipment.length})`}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'grid',
                                gap: '10px',
                                '@container equipment-plan-summary (min-width: 700px)': {
                                    gridTemplateColumns: 'repeat(3, 1fr)'
                                },
                                '@container equipment-plan-summary (max-width: 699px)': {
                                    gridTemplateColumns: 'repeat(2, 1fr)'
                                },
                                '@container equipment-plan-summary (max-width: 399px)': {
                                    gridTemplateColumns: '1fr'
                                }
                            }}
                        >
                            {equipment.map((item: IEquipmentClassUse, idx) => (
                                <Box
                                    key={`equipment-item-${idx}`}
                                    display="flex"
                                    flexDirection="column"
                                    p="2px"
                                >
                                    <Typography
                                        color={theme.palette.primary.contrastText}
                                        fontSize="0.75rem"
                                        fontWeight={300}
                                    >
                                        {formatDisplayString(item.name)}
                                    </Typography>
                                    {baseDefinedAndNotNullNarrowing(item.notes) && (
                                        <Typography
                                            color={theme.palette.primary.contrastText}
                                            fontSize="0.5rem"
                                            fontWeight={300}
                                        >
                                            {formatDisplayString(item.notes)}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )
            }
        </Box>
    );
};